import { inject, Injectable } from '@angular/core';
import { AuthorizationService, DefaultResourceProvider, PermissionService, Resource } from '@pure/authz-authorizer';
import { combineLatest, distinctUntilChanged, isObservable, map, Observable, of, shareReplay, switchMap } from 'rxjs';

/**
 * AuthorizationService.isAllowed() builds a new `shareReplay(1)` pipeline (no refCount) on every call, and
 * each one stays subscribed to the default resource and the permission cache after its subscribers leave.
 * The isAllowedImpure pipe calls it on every change-detection check, so every rendered use leaked one
 * pipeline per tick. This returns one pipeline per permission and resource, so the pipe's AsyncPipe keeps
 * its subscription instead of resubscribing, and the pipeline is refCounted, so it lets go of its upstreams
 * once its last subscriber leaves.
 *
 * The pipeline is the library's own, with `refCount: true`; the library's fields are private, so it cannot
 * be reused through `super`. refCount is safe because both upstreams replay their latest value (the
 * permission cache keeps a ReplaySubject per resource and domain), so a re-subscription gets the current
 * answer straight away and makes no new request.
 *
 * isAllowedAny() and isAllowedEvery() are memoised the same way: the library builds a fresh combineLatest +
 * shareReplay(1) per call around isAllowed(), so the isAllowedAnyImpure and isAllowedNoneImpure pipes would
 * otherwise still create a pipeline per check.
 *
 * Provided LAST in AppModule's providers. Its own providers override the ones AuthorizerModule.forRoot()
 * contributes, but importProvidersFrom(NotificationCenterModule.forRoot()) in the same array re-provides the
 * library's AuthorizationService, and for one token the last provider wins, so this entry must follow every
 * importProvidersFrom(). The provider spec checks AppModule's order.
 */
@Injectable()
export class MemoizingAuthorizationService extends AuthorizationService {
    private readonly defaultResource = inject(DefaultResourceProvider);
    private readonly permissions = inject(PermissionService);
    private readonly isAllowedCache = new Map<string, Observable<boolean>>();
    private readonly combinedCache = new Map<string, Observable<boolean>>();

    override isAllowed(permission: string, resource?: Resource | Observable<Resource>): Observable<boolean> {
        // An observable resource has no stable key, so it keeps the library's per-call behaviour.
        if (isObservable(resource)) {
            return super.isAllowed(permission, resource);
        }
        const key = resource ? `${permission}|${resource.type}:${resource.id}` : permission;
        let isAllowed$ = this.isAllowedCache.get(key);
        if (!isAllowed$) {
            const [domain] = permission.split(':');
            const resource$ = resource ? of(resource) : this.defaultResource.getDefaultResource();
            isAllowed$ = resource$.pipe(
                switchMap(r => this.permissions.getPermissions(r, domain)),
                map(permissions => permissions.includes(permission)),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
            this.isAllowedCache.set(key, isAllowed$);
        }
        return isAllowed$;
    }

    override isAllowedAny(permissions: string[], resource?: Resource | Observable<Resource>): Observable<boolean> {
        if (isObservable(resource)) {
            return super.isAllowedAny(permissions, resource);
        }
        return this.combine('any', permissions, resource, results => results.some(Boolean));
    }

    override isAllowedEvery(permissions: string[], resource?: Resource | Observable<Resource>): Observable<boolean> {
        if (isObservable(resource)) {
            return super.isAllowedEvery(permissions, resource);
        }
        return this.combine('every', permissions, resource, results => results.every(Boolean));
    }

    private combine(
        kind: 'any' | 'every',
        permissions: string[],
        resource: Resource | undefined,
        reduce: (results: boolean[]) => boolean,
    ): Observable<boolean> {
        const key = `${kind}|${permissions.join(',')}|${resource ? `${resource.type}:${resource.id}` : ''}`;
        let combined$ = this.combinedCache.get(key);
        if (!combined$) {
            combined$ = combineLatest(permissions.map(permission => this.isAllowed(permission, resource))).pipe(
                map(reduce),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
            this.combinedCache.set(key, combined$);
        }
        return combined$;
    }
}
